import { TrackingEvent, useAnalytics, useCurrentUserQuery } from '@alice-financial/alice-sdk'
import * as React from 'react'
import { isUserProfileType, useAvailableProfileTypes } from './sessionProfileHelpers'
import { ProfileTypeContextType, UserProfileType } from './types'

const CURRENT_PROFILE_TYPE_KEY = 'userProfileType'
const getKey = (userId: number | undefined) => `${CURRENT_PROFILE_TYPE_KEY}_${userId}`

const getStoredType = (userId: number | undefined): UserProfileType | null => {
  if (userId === undefined) return null
  const profileType = window.localStorage.getItem(getKey(userId))
  if (profileType && isUserProfileType(profileType)) return profileType
  return null
}
const storeType = (userId: number | undefined, profileType: UserProfileType) => {
  if (userId === undefined) return
  window.localStorage.setItem(getKey(userId), profileType)
}

// Export context directly for use in useProfileTypeContext hook
const ProfileTypeContext = React.createContext<ProfileTypeContextType | undefined>(undefined)

type ProviderProps = {
  children?: React.ReactNode
}

export const SessionProfileProvider: React.FC<ProviderProps> = ({ children }) => {
  const analytics = useAnalytics()
  const { data: currentUserData } = useCurrentUserQuery()
  const userId = currentUserData?.currentUser?.id
  const availableProfileTypes = useAvailableProfileTypes()

  const [currentType, setCurrentType] = React.useState<UserProfileType | null | undefined>(
    getStoredType(userId)
  )

  React.useEffect(() => {
    if (availableProfileTypes === undefined) {
      // Initialize or update based on available types and storage
      setCurrentType(undefined) // Still loading available types
      return
    }
    if (availableProfileTypes === null) {
      setCurrentType(null) // Error fetching available types
      return
    }

    if (!currentUserData) return
    const storedProfileType = getStoredType(userId)

    if (storedProfileType && availableProfileTypes.includes(storedProfileType)) {
      // Use stored type if valid and available
      if (currentType !== storedProfileType) {
        setCurrentType(storedProfileType)
      }
    } else {
      // Otherwise, use the highest priority available type
      const initialProfileType = availableProfileTypes[0]
      setCurrentType(initialProfileType)
      storeType(userId, initialProfileType) // Persist the default
    }
  }, [currentUserData, userId, availableProfileTypes, currentType])

  const setProfileType = React.useCallback(
    (profileType: UserProfileType) => {
      if (!userId) return // Cannot set without user ID

      // Need to re-check available types inside the callback's closure
      const currentAvailableTypes = availableProfileTypes
      if (currentAvailableTypes && currentAvailableTypes.includes(profileType)) {
        analytics.track(TrackingEvent.ProfileTypeChanged, { profileType })
        setCurrentType(profileType)
        storeType(userId, profileType)
      } else {
        const availableTypesString = currentAvailableTypes?.join(', ')
        console.warn(
          `Attempted to set unavailable profile type: ${profileType}. Available: ${availableTypesString}`
        )
      }
    },
    [userId, availableProfileTypes, analytics]
  )

  const contextValue = React.useMemo<ProfileTypeContextType>(
    () => ({
      current: currentType,
      setCurrent: setProfileType,
      available: availableProfileTypes,
    }),
    [currentType, setProfileType, availableProfileTypes]
  )

  return <ProfileTypeContext.Provider value={contextValue}>{children}</ProfileTypeContext.Provider>
}

export const useProfileTypeContext = (): ProfileTypeContextType => {
  const context = React.useContext(ProfileTypeContext)
  if (context === undefined) {
    throw new Error('useProfileTypeContext must be used within a ProfileTypeProvider')
  }
  return context
}
